import React, { useCallback, useEffect, useState } from 'react';
import { useEditor, EditorContent, ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { Node } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import UnderlineExtension from '@tiptap/extension-underline';
import LinkExtension from '@tiptap/extension-link';
import ImageExtension from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { TableKit } from '@tiptap/extension-table/kit';
import { marked } from 'marked';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, AlignLeft, AlignCenter, AlignRight, Table as TableIcon,
  Link as LinkIcon, ImagePlus, SeparatorHorizontal, RemoveFormatting, Undo2, Redo2,
  Check, X,
} from 'lucide-react';

// A page break is a real thing in a .docx, so it needs to be a real node rather
// than a styling trick. It serializes to <hr data-page-break="true">, which the
// Rails sanitizer allowlists and _html_to_docx turns into a w:br type="page".
const PageBreak = Node.create({
  name: 'pageBreak',
  group: 'block',
  parseHTML() {
    return [{ tag: 'hr[data-page-break]' }];
  },
  renderHTML() {
    return ['hr', { 'data-page-break': 'true' }];
  },
  addCommands() {
    return {
      setPageBreak: () => ({ commands }) => commands.insertContent({ type: this.name }),
    };
  },
});

// Image carrying the governance identity plus the sizing and alignment authors
// expect from Word. src is display only: the backend always resolves bytes by
// data-training-image-id, never by fetching the URL.
const TrainingImage = ImageExtension.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      'data-training-image-id': { default: null },
      'data-status': { default: null },
      'data-caption': { default: null },
      width: { default: null },
      align: { default: null },
    };
  },

  // Without a node view the caption is invisible while writing, because a
  // caption is not part of the content HTML: it lives on the image record and
  // only the docx renderer reads it. An editor that shows something different
  // from the download is the whole problem this rewrite set out to fix.
  addNodeView() {
    return ReactNodeViewRenderer(ImageFigureView);
  },
});

// Renders the image with its caption beneath, mirroring the .docx layout
// (centered picture, italic caption). data-caption is display-only; editing a
// caption goes through the image record, not through this attribute.
function ImageFigureView({ node, selected }) {
  const attrs = node.attrs || {};
  const caption = attrs['data-caption'];
  const pending = attrs['data-status'] === 'draft';
  const align = attrs.align || 'center';
  const justify = align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center';

  return (
    <NodeViewWrapper
      as="figure"
      className="my-3"
      style={{ display: 'flex', flexDirection: 'column', alignItems: justify }}
      data-drag-handle
    >
      <img
        src={attrs.src}
        alt={attrs.alt || ''}
        width={attrs.width || undefined}
        style={{
          maxWidth: '100%',
          borderRadius: 8,
          outline: pending ? '2px dashed #f7a23b' : selected ? '2px solid #1f6feb' : 'none',
          outlineOffset: 2,
        }}
      />
      <figcaption
        style={{
          fontSize: 11, fontStyle: 'italic', marginTop: 4,
          color: caption ? '#8a95a8' : '#5f6b7e',
          textAlign: 'center',
        }}
      >
        {caption || 'No caption. Use the caption button to add one.'}
        {pending && <span style={{ color: '#f7a23b' }}> (pending approval)</span>}
      </figcaption>
    </NodeViewWrapper>
  );
}

// The docx template owns typography, so there is deliberately no font family or
// size control here: author-chosen fonts would break consistency across the
// class library.
const EXTENSIONS = [
  StarterKit.configure({ link: false, underline: false }),
  UnderlineExtension,
  LinkExtension.configure({ openOnClick: false }),
  TrainingImage.configure({ inline: false, allowBase64: false }),
  // Serialize to a data attribute, NOT to inline style: the Rails sanitizer
  // strips style, so the default renderHTML would lose alignment on every save.
  TextAlign.configure({
    types: ['heading', 'paragraph'],
    alignments: ['left', 'center', 'right', 'justify'],
  }).extend({
    addGlobalAttributes() {
      return [{
        types: ['heading', 'paragraph'],
        attributes: {
          textAlign: {
            default: null,
            parseHTML: (el) => el.getAttribute('data-text-align'),
            renderHTML: (attrs) =>
              (attrs.textAlign ? { 'data-text-align': attrs.textAlign } : {}),
          },
        },
      }];
    },
  }),
  TableKit.configure({ table: { resizable: true } }),
  PageBreak,
];

// Word puts a mountain of mso-* styling on the clipboard. Strip it so a paste
// does not arrive carrying attributes the sanitizer will drop anyway.
function cleanWordHtml(html) {
  return String(html || '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<\/?(o:p|w:[a-z]+|m:[a-z]+)[^>]*>/gi, '')
    .replace(/\s(class|style|lang)="[^"]*"/gi, '')
    .replace(/\sxmlns[^=]*="[^"]*"/gi, '')
    .replace(/<span[^>]*>\s*<\/span>/gi, '');
}

export default function ArtifactEditor({
  value, format, onSave, onCancel, onEditorReady, onInsertImage, renderBubbleMenu,
}) {
  // Legacy artifacts are still Markdown until their first save. Convert on load
  // so the author never sees syntax; the first save persists HTML.
  const initial = format === 'html'
    ? (value || '')
    : marked(value || '', { breaks: true, gfm: true });

  const [dirty, setDirty] = useState(false);

  const editor = useEditor({
    extensions: EXTENSIONS,
    content: initial,
    onUpdate: () => setDirty(true),
    editorProps: {
      attributes: {
        class: 'px-4 py-3 text-sm min-h-[360px] max-h-[52vh] overflow-y-auto focus:outline-none',
      },
      transformPastedHTML: cleanWordHtml,
    },
  });

  useEffect(() => {
    if (editor && onEditorReady) onEditorReady(editor);
  }, [editor, onEditorReady]);

  // Browser-level guard for closing the tab or reloading. Saving is deliberate
  // here (it returns the artifact to draft), so a lost edit is a real cost.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const addLink = useCallback(() => {
    const url = window.prompt('Link URL');
    if (!url) return;
    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  const btn = (active) =>
    `p-1.5 rounded transition-colors ${active
      ? 'bg-blue-600 text-white'
      : 'text-[#8a95a8] hover:bg-[#12202f] hover:text-[#e6edf6]'}`;
  const divider = <div className="w-px h-4 mx-1 bg-[#2b3a4d]" />;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 rounded-t-lg border border-b-0 border-[#2b3a4d] bg-[#0f1620]">
        <button onClick={() => editor.chain().focus().undo().run()} className={btn(false)} title="Undo"><Undo2 size={14} /></button>
        <button onClick={() => editor.chain().focus().redo().run()} className={btn(false)} title="Redo"><Redo2 size={14} /></button>
        {divider}
        <button onClick={() => editor.chain().focus().toggleBold().run()} className={btn(editor.isActive('bold'))} title="Bold"><Bold size={14} /></button>
        <button onClick={() => editor.chain().focus().toggleItalic().run()} className={btn(editor.isActive('italic'))} title="Italic"><Italic size={14} /></button>
        <button onClick={() => editor.chain().focus().toggleUnderline().run()} className={btn(editor.isActive('underline'))} title="Underline"><UnderlineIcon size={14} /></button>
        <button onClick={() => editor.chain().focus().toggleStrike().run()} className={btn(editor.isActive('strike'))} title="Strikethrough"><Strikethrough size={14} /></button>
        {divider}
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={btn(editor.isActive('heading', { level: 1 }))} title="Heading 1"><Heading1 size={14} /></button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btn(editor.isActive('heading', { level: 2 }))} title="Heading 2"><Heading2 size={14} /></button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={btn(editor.isActive('heading', { level: 3 }))} title="Heading 3"><Heading3 size={14} /></button>
        {divider}
        <button onClick={() => editor.chain().focus().setTextAlign('left').run()} className={btn(editor.isActive({ textAlign: 'left' }))} title="Align left"><AlignLeft size={14} /></button>
        <button onClick={() => editor.chain().focus().setTextAlign('center').run()} className={btn(editor.isActive({ textAlign: 'center' }))} title="Align center"><AlignCenter size={14} /></button>
        <button onClick={() => editor.chain().focus().setTextAlign('right').run()} className={btn(editor.isActive({ textAlign: 'right' }))} title="Align right"><AlignRight size={14} /></button>
        {divider}
        <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(editor.isActive('bulletList'))} title="Bullet list"><List size={14} /></button>
        <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(editor.isActive('orderedList'))} title="Numbered list"><ListOrdered size={14} /></button>
        <button onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btn(editor.isActive('blockquote'))} title="Quote"><Quote size={14} /></button>
        {divider}
        <button onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} className={btn(editor.isActive('table'))} title="Insert table"><TableIcon size={14} /></button>
        <button onClick={addLink} className={btn(editor.isActive('link'))} title="Insert link"><LinkIcon size={14} /></button>
        {onInsertImage && (
          <button onClick={onInsertImage} className={btn(false)} title="Insert image"><ImagePlus size={14} /></button>
        )}
        <button onClick={() => editor.chain().focus().setPageBreak().run()} className={btn(false)} title="Page break"><SeparatorHorizontal size={14} /></button>
        <button onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} className={btn(false)} title="Clear formatting"><RemoveFormatting size={14} /></button>
      </div>

      <div className="rounded-b-lg border border-[#2b3a4d] bg-[#0b0e13] text-[#c7d0e0]
        [&_.ProseMirror_h1]:text-lg [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:mt-3
        [&_.ProseMirror_h2]:text-base [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:mt-3
        [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:mt-2
        [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5
        [&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-[#2b3a4d] [&_.ProseMirror_blockquote]:pl-3 [&_.ProseMirror_blockquote]:italic
        [&_.ProseMirror_table]:w-full [&_.ProseMirror_table]:border-collapse
        [&_.ProseMirror_th]:border [&_.ProseMirror_th]:border-[#2b3a4d] [&_.ProseMirror_th]:bg-[#12202f] [&_.ProseMirror_th]:px-2 [&_.ProseMirror_th]:py-1
        [&_.ProseMirror_td]:border [&_.ProseMirror_td]:border-[#2b3a4d] [&_.ProseMirror_td]:px-2 [&_.ProseMirror_td]:py-1
        [&_.ProseMirror_hr]:border-t [&_.ProseMirror_hr]:border-[#2b3a4d] [&_.ProseMirror_hr]:my-3
        [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:rounded-lg [&_.ProseMirror_img]:my-2">
        <EditorContent editor={editor} />
        {renderBubbleMenu?.(editor)}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            const html = editor.getHTML();
            // Guard against writing an empty document over a non-empty artifact.
            // A field-name mismatch once made the editor load blank, and Save
            // then silently destroyed the body. Never let that be silent again.
            const isEmpty = !editor.getText().trim() && !html.includes('<img');
            const hadContent = Boolean((value || '').trim());
            if (isEmpty && hadContent
              && !window.confirm('This will save an empty document and erase the current content. Continue?')) {
              return;
            }
            setDirty(false);
            onSave(html);
          }}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#1f6feb] text-white"
        >
          <Check size={12} /> Save
        </button>
        <button
          onClick={() => {
            if (dirty && !window.confirm('Discard your unsaved changes?')) return;
            onCancel();
          }}
          className="flex items-center gap-1 text-xs px-3 py-1.5 text-[#8a95a8]"
        >
          <X size={12} /> Cancel
        </button>
        <span className="text-[10px] text-[#5f6b7e] ml-2">
          Saving returns this artifact to draft for re-approval.
        </span>
      </div>
    </div>
  );
}
